<template>
    <div class="company-warehouse-list-table">
        <el-table
            :data="processingQueue"
            :empty-text="translate('no_data')"
            highlight-current-row
            v-loading="isLoading"
        >
            <el-table-column
                min-width="100px"
                prop="codigoLyra"
                :label="translate('lyra_id')"
                label-class-name="company-warehouse-list-table_label"
            />
            <el-table-column
                min-width="100px"
                prop="senave"
                :label="translate('document_number')"
                label-class-name="company-warehouse-list-table_label"
            />
            <el-table-column
                min-width="150px"
                prop="unidade"
                :label="translate('facility_name')"
                label-class-name="company-warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="company-warehouse-list_text">
                        {{ scope.row.unidade }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="150px"
                prop="razaoSocial"
                :label="translate('facility_corporate_name')"
                label-class-name="company-warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="company-warehouse-list_text">
                        {{ scope.row.razaoSocial }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="100px"
                prop="tipoArmazem"
                :label="translate('facility_type')"
                label-class-name="company-warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div>
                        {{ getFaciltyType(scope.row.tipoArmazem) }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="150px"
                prop="endereco"
                :label="translate('location')"
                label-class-name="company-warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div class="company-warehouse-list_text">
                        {{ scope.row.endereco }}
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                min-width="80px"
                prop="empresas"
                :label="translate('companies')"
                label-class-name="company-warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <div>
                        <span v-if="scope.row.empresas.length <= 1">
                            {{ getCompanyName(scope.row.empresas[0]) }}
                        </span>
                        <div v-else>
                            <span>{{ getCompanyName(scope.row.empresas[0]) }}</span>
                            <el-popover
                                placement="top-start"
                                :width="200"
                                :title="translate('linked_companies')"
                                trigger="hover"
                            >
                                <template #reference>
                                    <el-tag class="ml-2" size="mini">
                                        {{ scope.row.empresas.length }}
                                    </el-tag>
                                </template>
                                <div>
                                    <div
                                        v-for="company in scope.row.empresas"
                                        class="company-list-item"
                                        :key="company"
                                    >
                                        {{ getCompanyName(company) }}
                                    </div>
                                </div>
                            </el-popover>
                        </div>
                    </div>
                </template>
            </el-table-column>
            <el-table-column
                :label="translate('actions')"
                align="center"
                label-class-name="company-warehouse-list-table_label"
            >
                <template v-slot:default="scope">
                    <el-dropdown
                        class="action-button"
                        trigger="click"
                        @click="scope"
                    >
                        <span class="el-dropdown-link">
                            <i class="mdi mdi-dots-vertical" />
                        </span>
                        <el-dropdown-menu v-slot="dropdown">
                            <el-dropdown-item
                                icon="el-icon-view"
                                @click.native="handleView(scope.row)"
                            >
                                {{ $t('warehouse.messages.view_facility') }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="scope.row.tipoArmazem === 'FABRICA' && canEditTransformationPercentage(scope.row)"
                                icon="el-icon-edit"
                                @click.native="openTransformationModal(scope.row)"
                            >
                                {{ $t('warehouse.messages.transformation_percentage') }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </template>
            </el-table-column>
        </el-table>
        <div class="company-warehouse-list-table__pagination" v-if="!isLoading">
            <div class="total">
                <span>{{ totalElements }}
                    {{
                        translate('results')
                    }}</span>
                <div style="margin: 0 20px"></div>
                <span>{{
                        translate('views_by_page')
                    }}:</span>
                <el-pagination
                    @size-change="handleSizeChange"
                    :current-page="page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="size"
                    layout="sizes"
                    :total="totalElements"
                />
            </div>
            <div class="prev_next">
                <el-pagination
                    @current-change="handleCurrentChange"
                    :current-page.sync="page"
                    :page-sizes="[10, 20, 30, 40]"
                    :page-size="size"
                    layout="prev, pager, next, jumper"
                    :total="totalElements"
                />
            </div>
        </div>
        <TransformationPercentageModal
            :show.sync="showTransformationModal"
            :factory="selectedFactory"
        />
    </div>
</template>

<script>
import ArmazemModuleService from '@/services/ArmazemModuleService';
import { mapGetters, mapActions } from 'vuex';
import TransformationPercentageModal from '../transform-modal/TransformationPercentageModal.vue';

export default {
    name: 'fileDefault',
    components: {
        TransformationPercentageModal
    },
    props: {
        filters: {
            type: Object,
            required: false,
            default: () => null
        }
    },
    data() {
        return {
            processingQueue: [],
            page: 1,
            size: 10,
            totalElements: 0,
            filteredCompanies: [],
            showTransformationModal: false,
            selectedFactory: null
        };
    },
    computed: {
        ...mapGetters(['empresasSubEmpresasPorPais', 'usuarioLogado']),
        isLoading() {
            return this.filteredCompanies.length === 0 && this.processingQueue.length === 0;
        }
    },
    async mounted() {
        if (this.empresasSubEmpresasPorPais.length === 0) {
            await this.fetchCompaniesAndSubCompanies();
        } else {
            this.filteredCompanies = this.empresasSubEmpresasPorPais;
        }
        this.fetchingCompanyWarehouseList();
    },
    watch: {
        filters: {
            handler(newVal) {
                this.fetchingCompanyWarehouseList();
            },
            deep: true
        }
    },
    methods: {
        ...mapActions(['fetchEmpresasSubEmpresasPorPais']),

        translate(key, params = {}) {
            const base = 'warehouse.warehouse_list_page.table';
            return this.$t(`${base}.${key}`, params);
        },
        getFaciltyType(type) {
            if (!type || type === '') return '-';
            return this.$t(`warehouse.messages.facility_types.${type.toLowerCase()}`);
        },
        canEditTransformationPercentage(row) {
            if (!this.usuarioLogado || !this.usuarioLogado.idEmpresasFiltro || !row.empresas) {
                return false;
            }
            return row.empresas.some(idEmpresa => this.usuarioLogado.idEmpresasFiltro.includes(idEmpresa));
        },
        openTransformationModal(row) {
            if (row && row.tipoArmazem === 'FABRICA') {
                this.selectedFactory = row;
                this.showTransformationModal = true;
            }
        },
        handleSizeChange(size) {
            this.page = 1;
            this.size = size;
            this.fetchingCompanyWarehouseList();
        },
        handleCurrentChange(page) {
            this.page = page;
            this.fetchingCompanyWarehouseList();
        },
        async fetchingCompanyWarehouseList() {
            if (this.filters) {
                ArmazemModuleService.getWarehouseByCompanyFiltered(
                    {
                        searchText: this.filters.searchText,
                        tipoArmazemCodigo: this.filters.warehouseType.length > 0 ? this.filters.warehouseType[0] : '',
                        empresaId: this.filters.selectedCompanyId
                    },
                    (this.page - 1), this.size
                ).then(({ data }) => {
                    this.processingQueue = data.itens;
                    this.totalElements = data.totalItens;
                });
            }
        },
        async fetchCompaniesAndSubCompanies() {
            if (this.empresasSubEmpresasPorPais.length === 0) {
                await this.fetchEmpresasSubEmpresasPorPais(this.usuarioLogado.paisGrupo ? this.usuarioLogado.paisGrupo : 'PY');
            }
            this.filteredCompanies = this.empresasSubEmpresasPorPais;
        },
        getCompanyName(companyId) {
            let company = this.filteredCompanies.filter(company => {
                return company.id === companyId;
            });

            return company[0].name;
        },
        handleView(warehouse) {
            if (warehouse.tipoArmazem === 'PORTO') {
                this.$router.push({
                    name: 'VisualizarPorto',
                    params: {
                        warehouseId: warehouse.id,
                        viewPendingOperations: 'transacoes-porto',
                        type: 'PORT'
                    }
                });
            } else if (warehouse.tipoArmazem === 'FABRICA') {
                this.$router.push({
                    name: 'VisualizarFabrica',
                    params: {
                        factoryId: warehouse.id,
                        viewPendingOperations: 'transacoes-fabrica',
                        type: 'FACTORY'
                    }
                });
            } else {
                this.$router.push({
                    name: 'VisualizarArmazem',
                    params: {
                        warehouseId: warehouse.id,
                        viewPendingOperations: 'transacoes-armazem',
                        type: 'WAREHOUSE'
                    }
                });
            }
        }
    }
};
</script>

<style scoped lang="scss">
.company-warehouse-list-table {
    margin-top: -8px;
    .el-table {
        .action-button {
            cursor: pointer;
        }
    }
    .company-warehouse-list-table__pagination {
        display: grid;
        grid-template-columns: 2fr auto;
        margin-top: 20px;
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
        }

        .total {
            display: flex;
            align-items: center;
            @media (max-width: 768px) {
                flex-direction: column;
            }
        }

        .prev_next {
            display: flex;
            @media (max-width: 768px) {
                justify-content: center;
                margin-top: 20px;
            }
        }
    }
}
.level1-label {
    font-size: 12px;
    color: #7c7c7c !important;
}
::v-deep .company-warehouse-list-table_label {
    word-break: keep-all !important;
}
.company-warehouse-list_text {
    word-break: keep-all !important;
}
.file-list_text {
    word-break: keep-all !important;
}
.tag-status-solicitado {
    color: #525252;
    background-color: transparent;
    border: 1px solid #E0E0E0;
    border-radius: 4px !important;
}
.tag-status-em_processamento {
    color: #525252;
    background-color: #F5F5F5;
    border: 1px solid #E0E0E0;
    border-radius: 4px !important;
}
.tag-status-processado {
    color: #1F5B21;
    background-color: #EBF4EB;
    border: 1px solid #C0DDC1;
    border-radius: 4px !important;
}
.tag-status-processado_com_erro {
    color: #6D4D09;
    background-color: #FDF7E8;
    border: 1px solid #F9E5B9;
    border-radius: 4px !important;
}
.tag-status-erro {
    color: #991F1A;
    background-color: #FCEBEA;
    border: 1px solid #F4C1BE;
    border-radius: 4px !important;
}
.table_second-row-label {
    color: #7C7C7C !important;
    font-size: 12px;
}
.company-list-item {
    font-size: 11px;
}
</style>
